import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { Paper, Box, useTheme, useMediaQuery } from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';
import { darkColors } from '../../../theme/palette';

import {
  HpLevel,
  Inverter,
  SpotPriceLevel,
  SpotPricePeriod,
} from '../../../../../core/src/shared/enums';

import { useSelector } from '../../../store/utils';
import { RouterOutput } from '../../../api/trpc';

import { ColBoolean } from './col-boolean';
import { ColPriceLevel } from './col-price-level';
import { ColHeatPumpLevel } from './col-heat-pump-level';
import {
  SpotPricesChart,
  SpotPricesChartAxisY,
} from '../../../molecules/spot-prices-chart';
import { useFormatNumber } from '../../../utils/format-number';
import { SpotSummaryRow, RowMessage } from './spot-summary-row';
import { messages, tooltipMessages } from './spot-summary.messages';
import {
  hoverGroupSx,
  overlayGroupSx,
  tipShiftSx,
  tooltipSx,
  headSx,
} from './spot-summary.styles';
import {
  SpotPriceView,
  useSpotSummary,
} from '../../../molecules/use-spot-summary';

const summaryHead = Array.from({ length: 24 }, (_, i) => i + 1);

// The dark Paper renders at `elevation1`, which composites a ~5% white overlay
// over `darkColors.paper`. `lighten(color, 0.05)` reproduces that exact surface
// color — the value the inactive-column veil must match to stay invisible over
// the empty space above each bar.
const darkPaperElevated = lighten(darkColors.paper, 0.05);

type Summary = NonNullable<RouterOutput['spot']['getSummary']['summary']>;

// One descriptor per optional feature row: its visibility condition and the data
// behind it. Single source of truth for both the rendered rows and the tooltip
// lines, so a condition like `batteryChargeFromGridEnabled && batteryControl`
// lives in exactly one place.
type SpotFeature =
  | {
      kind: 'bool';
      visible: boolean;
      title: RowMessage;
      help: RowMessage;
      data60M: boolean[];
      data15M: boolean[];
      // Tooltip status text; defaults to generic Active/Inactive when omitted.
      trueLabel?: RowMessage;
      falseLabel?: RowMessage;
    }
  | {
      kind: 'hpLevel';
      visible: boolean;
      title: RowMessage;
      help: RowMessage;
      data60M: HpLevel[];
    };

const getFeatures = (summary: Summary, hes: boolean): SpotFeature[] => {
  const {
    spotOverflow: spotOverflow60M,
    spotOverflow15M,
    batteryDischarge,
    batteryDischarge15M,
    batteryChargeFromGrid,
    batteryChargeFromGrid15M,
    batteryDischargeToGrid,
    batteryDischargeToGrid15M,
    hwHeatingFromGrid: hwHeatingFromGrid60M,
    hwHeatingFromGrid15M,
    heatPumpLevel,
    batteryChargeFromGridEnabled,
    batteryDischargeToGridEnabled,
    batteryControl,
    hwInstalled,
    hpInstalled,
  } = summary;

  return [
    {
      kind: 'bool',
      visible: !hes,
      title: messages.spotOverflowTitle,
      help: messages.spotOverflowHelp,
      data60M: spotOverflow60M,
      data15M: spotOverflow15M,
      trueLabel: tooltipMessages.spotOverflowAllowed,
      falseLabel: tooltipMessages.spotOverflowRestricted,
    },
    {
      kind: 'bool',
      visible: batteryControl,
      title: messages.batteryDischargeTitle,
      help: messages.batteryDischargeHelp,
      data60M: batteryDischarge,
      data15M: batteryDischarge15M,
    },
    {
      kind: 'bool',
      visible: batteryChargeFromGridEnabled && batteryControl,
      title: messages.batteryChargeFromGridTitle,
      help: messages.batteryChargeFromGridHelp,
      data60M: batteryChargeFromGrid,
      data15M: batteryChargeFromGrid15M,
    },
    {
      kind: 'bool',
      visible: batteryDischargeToGridEnabled && batteryControl,
      title: messages.batteryDischargeToGridTitle,
      help: messages.batteryDischargeToGridHelp,
      data60M: batteryDischargeToGrid,
      data15M: batteryDischargeToGrid15M,
    },
    {
      kind: 'bool',
      visible: hwInstalled,
      title: messages.boilerTitle,
      help: messages.boilerHelp,
      data60M: hwHeatingFromGrid60M,
      data15M: hwHeatingFromGrid15M,
    },
    {
      kind: 'hpLevel',
      visible: hpInstalled,
      title: messages.heatPumpTitle,
      help: messages.heatPumpHelp,
      data60M: heatPumpLevel,
    },
  ];
};

type Props = {
  summary: ReturnType<typeof useSpotSummary>['summary'];
  summaryLoading: boolean;
  summaryError: boolean;
  priceView: SpotPriceView;
};

export const SpotSummary = ({
  summary,
  summaryLoading,
  summaryError,
  priceView,
}: Props) => {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const sm = useMediaQuery(theme.breakpoints.up('sm'));
  const formatNumber = useFormatNumber();
  const intl = useIntl();

  const hes = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return inverterConfigArray[0]?.type === Inverter.Hes;
  });

  const spotPricePeriod = useSelector(
    ({ variables: { SP_PRICE_PERIOD } }) => SP_PRICE_PERIOD
  );
  const currency = useSelector((s) => s.variables.CURRENCY);

  // Tomorrow's data only carries prices — none of the feature/component arrays
  // (price levels, battery, heat pump, …). In that case we render a slim
  // variant: chart, time and prices only, skipping every component row.
  const isTomorrow = summary?.variant === 'tomorrow';

  // Precompute every column's aggregate tooltip once — 24 hourly + 96
  // quarter-hour slots. The global store re-renders this component frequently;
  // memoizing keeps the costly intl/number formatting from running on every
  // render and only recomputes when the underlying data actually changes.
  const tooltipsByPeriod = useMemo((): Record<
    string,
    { full: string; short: string }[]
  > => {
    const empty: Record<string, { full: string; short: string }[]> = {
      [SpotPricePeriod['60M']]: [],
      [SpotPricePeriod['15M']]: [],
    };
    if (!summary) return empty;

    // Narrow away the tomorrow variant, which carries no feature/level data.
    const detail = summary.variant === 'tomorrow' ? undefined : summary;
    const spotPriceLevel = detail?.spotPriceLevel;
    const spotPriceLevel15M = detail?.spotPriceLevel15M;

    const prices = summary.spotPricesWithDistribution;
    const prices15M = summary.spotPrices15MWithDistribution;
    const basePrices = summary.spotPrices;
    const basePrices15M = summary.spotPrices15M;

    // Resolve every constant label/state string once for the whole grid.
    const priceBuyLabel = intl.formatMessage(tooltipMessages.priceBuy);
    const priceSellLabel = intl.formatMessage(tooltipMessages.priceSell);
    const priceLevelLabel = intl.formatMessage(messages.priceLevelTitle);
    const levelText = (lvl: SpotPriceLevel) =>
      intl.formatMessage(
        lvl === SpotPriceLevel.Hi
          ? tooltipMessages.priceLevelHi
          : lvl === SpotPriceLevel.Mid
          ? tooltipMessages.priceLevelMid
          : tooltipMessages.priceLevelLow
      );
    const hpText = (hp: HpLevel) =>
      intl.formatMessage(
        hp === HpLevel.Increase
          ? tooltipMessages.heatPumpIncrease
          : hp === HpLevel.Normal
          ? tooltipMessages.heatPumpNormal
          : tooltipMessages.heatPumpReduction
      );

    const features = detail
      ? getFeatures(detail, hes).filter((f) => f.visible)
      : [];
    // Resolve each feature's title and (for booleans) its on/off status text
    // once — a feature may override the generic Active/Inactive wording.
    const resolved = features.map((f) => ({
      label: intl.formatMessage(f.title),
      on:
        f.kind === 'bool'
          ? intl.formatMessage(f.trueLabel ?? tooltipMessages.stateActive)
          : '',
      off:
        f.kind === 'bool'
          ? intl.formatMessage(f.falseLabel ?? tooltipMessages.stateInactive)
          : '',
    }));

    const build = (periodIndex: number, is15M: boolean) => {
      const hour = is15M ? Math.floor(periodIndex / 4) : periodIndex;
      const minute = is15M ? (periodIndex % 4) * 15 : 0;
      const time = `${hour}:${String(minute).padStart(2, '0')}`;

      const fmt = (v: number) =>
        formatNumber(v, { style: 'currency', fractionDigitsOffset: 1 });
      // Buy price = spot price + distribution; sell price = bare spot price.
      const buy = (is15M ? prices15M : prices)?.[periodIndex];
      const sell = (is15M ? basePrices15M : basePrices)?.[periodIndex];

      // Header: time on the first line, the price for the active view toggle
      // (bare spot price, distribution surcharge alone, or their sum) on the
      // second, followed by a divider before the detail lines below.
      const headerPrice =
        priceView === SpotPriceView.PriceOnly
          ? sell
          : priceView === SpotPriceView.DistributionOnly
          ? buy != null && sell != null
            ? buy - sell
            : undefined
          : buy;
      // Short tooltip (chart bars): just the hour and the active-view price.
      const short = headerPrice != null ? `${time}\n${fmt(headerPrice)}` : time;

      const lines: string[] = [time];
      if (headerPrice != null) {
        lines.push(fmt(headerPrice));
      }
      lines.push('─────────');

      if (buy != null) {
        lines.push(`${priceBuyLabel}: ${fmt(buy)}`);
      }
      if (sell != null) {
        lines.push(`${priceSellLabel}: ${fmt(sell)}`);
      }

      // 15M-granular features read the 15M array at the slot; a feature without
      // a 15M array (e.g. heat pump) falls back to its hourly value.
      const at = <T,>(d60: T[], d15?: T[]): T | undefined =>
        is15M ? (d15 ? d15[periodIndex] : d60[hour]) : d60[periodIndex];

      const priceLevel = is15M
        ? spotPriceLevel15M?.[periodIndex]
        : spotPriceLevel?.[hour];
      if (priceLevel != null) {
        lines.push(`${priceLevelLabel}: ${levelText(priceLevel)}`);
      }

      features.forEach((f, fi) => {
        const r = resolved[fi];
        if (f.kind === 'bool') {
          const value = at(f.data60M, f.data15M);
          if (value != null) {
            lines.push(`${r.label}: ${value ? r.on : r.off}`);
          }
        } else {
          const value = at(f.data60M);
          if (value != null) {
            lines.push(`${r.label}: ${hpText(value)}`);
          }
        }
      });

      return { full: lines.join('\n'), short };
    };

    return {
      [SpotPricePeriod['60M']]: Array.from({ length: 24 }, (_, i) =>
        build(i, false)
      ),
      [SpotPricePeriod['15M']]: Array.from({ length: 96 }, (_, i) =>
        build(i, true)
      ),
    };
    // formatNumber is intentionally excluded from the deps: its identity changes
    // every render, but its output only depends on `intl` + `currency` below.
  }, [summary, hes, intl, currency, isTomorrow, priceView]);

  if (!summary) {
    return null;
  }

  // Narrow away the tomorrow variant, which carries no feature/level data.
  const detail = summary.variant === 'tomorrow' ? undefined : summary;
  const spotPriceLevel = detail?.spotPriceLevel;
  const spotPriceLevel15M = detail?.spotPriceLevel15M;

  // The price row shows whichever value the user picked in the view toggle:
  // bare spot price, distribution surcharge alone, or their sum.
  const prices = summary.spotPrices.map((base, i) => {
    const withDistribution = summary.spotPricesWithDistribution[i] ?? base;
    if (priceView === SpotPriceView.PriceOnly) return base;
    if (priceView === SpotPriceView.DistributionOnly)
      return withDistribution - base;
    return withDistribution;
  });

  // Cheap lookup into the memoized tooltips (built above). The summary cells
  // (time/price/feature rows) show the full tooltip; the chart bars pass
  // 'short' to show just the hour and price.
  const getTooltip = (
    periodIndex: number,
    pricePeriod: SpotPricePeriod,
    variant: 'full' | 'short' = 'full'
  ): string => tooltipsByPeriod[pricePeriod]?.[periodIndex]?.[variant] ?? '';

  // The time/price rows render fewer labels on small screens (cells need width
  // for the text), and the visible labels are spread evenly across the row. So
  // each label visually sits above an equal slice of bars regardless of its
  // hour value. Give every label the hover classes of the bars under it, so
  // hovering any bar highlights the label positioned above it. Time and price
  // share this visibility predicate (time cell i => hour i-1, price cell i => hour i).
  const isLabelVisible = (hour: number) =>
    !((!lg && (hour + 1) % 2 === 0) || (!sm && (hour + 1) % 3 === 0));

  const labelCoverageClass: Record<number, string> = {};
  {
    const visibleHours = Array.from({ length: 24 }, (_, h) => h).filter(
      isLabelVisible
    );
    const coveredBars: Record<number, number[]> = {};
    visibleHours.forEach((hour) => (coveredBars[hour] = []));
    for (let bar = 0; bar < 24; bar++) {
      const cell = Math.floor((bar * visibleHours.length) / 24);
      coveredBars[visibleHours[cell]].push(bar);
    }
    visibleHours.forEach((hour) => {
      labelCoverageClass[hour] = coveredBars[hour]
        .map((bar) => `spot-summary-col-60M-${bar}`)
        .join(' ');
    });
  }

  const is15M = spotPricePeriod === SpotPricePeriod['15M'];
  const features = detail ? getFeatures(detail, hes) : [];

  return (
    <Paper>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(96, 1fr)',
            lg: 'auto repeat(96, 1fr)',
          },
          '& [class*="spot-summary-col-"]': {
            // transition: 'opacity 0.15s',
            opacity: 1,
          },
          '&:has([class*="spot-summary-col-"]:hover) [class*="spot-summary-col-"]':
            {
              opacity: 0.3,
            },
          ...hoverGroupSx,
          '& [class*="spot-summary-col-"]:hover': {
            opacity: '1 !important',
          },
          // chart overlay: kept at full opacity (so the data-tooltip bubbles
          // show) with a transparent background, so the bars show through.
          '& .chart-overlay [class*="spot-summary-col-"]': {
            opacity: '1 !important',
            backgroundColor: 'transparent',
            // transition: 'background-color 0.15s',
          },
          // hovering any column (chart bar OR data cell) veils every bar with the
          // Paper background color (white in light mode, dark grey in dark mode)
          // so the bars fade toward the background rather than getting washed out.
          '&:has([class*="spot-summary-col-"]:hover) .chart-overlay [class*="spot-summary-col-"]':
            {
              backgroundColor: `${alpha(
                theme.palette.background.paper,
                0.55
              )} !important`,
              ...theme.applyStyles('dark', {
                // `darkColors.paper` (#2c2c2c) lifted by MUI's elevation1
                // overlay (white ~5%) = the *actual* rendered Paper surface.
                // Keying the veil to this (not the raw color) keeps it invisible
                // over the empty space above each bar while still dimming bars.
                backgroundColor: `${alpha(darkPaperElevated, 0.55)} !important`,
              }),
            },
          // ...except the bar for the hovered column, which stays clear
          ...overlayGroupSx,
          ...tipShiftSx,
          ...tooltipSx,
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            justifyContent: 'flex-end',
            pt: 1,
          }}
        >
          <SpotPricesChartAxisY summary={summary} priceView={priceView} />
        </Box>
        <Box sx={{ gridColumn: 'span 96', position: 'relative' }}>
          <SpotPricesChart
            summary={summary}
            summaryError={summaryError}
            summaryLoading={summaryLoading}
            priceView={priceView}
            hideAxisX
            sx={{
              '& > .MuiBox-root': { p: 0 },
              position: 'relative',
              zIndex: 1,
              bgcolor: 'transparent',
            }}
          />

          <Box
            className="chart-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'grid',
              zIndex: 2,
              gridTemplateColumns: `repeat(${
                spotPricePeriod === SpotPricePeriod['15M'] ? 96 : 24
              }, 1fr)`,
            }}
          >
            {Array.from(
              { length: spotPricePeriod === SpotPricePeriod['15M'] ? 96 : 24 },
              (_, i) => i
            ).map((i) => {
              return (
                <Box
                  className={`spot-summary-col-${spotPricePeriod}-${i}`}
                  key={i}
                  data-tooltip={getTooltip(i, spotPricePeriod, 'short')}
                />
              );
            })}
          </Box>
        </Box>

        <Box sx={{ ...headSx, mt: { xs: 1.25, lg: 0 } }}>
          <FormattedMessage id="spot.summary.time" defaultMessage="Čas" />
        </Box>
        {summaryHead.map((i) => {
          if (!lg && i % 2 === 0) return null;
          if (!sm && i % 3 === 0) return null;

          return (
            <Box
              component="span"
              key={i}
              sx={{
                textAlign: 'left',
                fontSize: 12,
                gridColumn: {
                  xs: 'span 12',
                  sm: 'span 8',
                  lg: 'span 4',
                },
                py: 0.5,
                width: 'auto',
              }}
              className={labelCoverageClass[i - 1]}
              data-tooltip={getTooltip(i - 1, SpotPricePeriod['60M'])}
            >
              <Box sx={{ height: { xs: 16, sm: 26 } }}>{i - 1}:00</Box>
            </Box>
          );
        })}

        <Box sx={{ ...headSx }}>
          <FormattedMessage id="spot.summary.price" defaultMessage="Cena" />
        </Box>
        {prices.map((value, i) => {
          if (!lg && (i + 1) % 2 === 0) return null;
          if (!sm && (i + 1) % 3 === 0) return null;

          return (
            <Box
              key={i}
              component="span"
              sx={{
                fontSize: 12,
                gridColumn: {
                  xs: 'span 12',
                  sm: 'span 8',
                  lg: 'span 4',
                },
                py: 0.5,
                width: 'auto',
              }}
              className={labelCoverageClass[i]}
              data-tooltip={getTooltip(i, SpotPricePeriod['60M'])}
            >
              <Box sx={{ height: { xs: 16, sm: 26 } }}>
                <FormattedNumber
                  value={value}
                  maximumFractionDigits={2}
                  minimumFractionDigits={2}
                />
              </Box>
            </Box>
          );
        })}

        {/* Feature rows: each renders a header (+ help) and a cell per period.
            Skipped entirely for tomorrow, which only has price data. */}
        {!isTomorrow && spotPriceLevel && (
          <SpotSummaryRow
            title={messages.priceLevelTitle}
            help={messages.priceLevelHelp}
            data={spotPriceLevel}
            pricePeriod={SpotPricePeriod['60M']}
            Cell={ColPriceLevel}
            getTooltip={getTooltip}
          />
        )}

        {!isTomorrow && is15M && spotPriceLevel15M && (
          <SpotSummaryRow
            title={messages.priceLevel15MTitle}
            data={spotPriceLevel15M}
            pricePeriod={SpotPricePeriod['15M']}
            Cell={ColPriceLevel}
            getTooltip={getTooltip}
          />
        )}

        {features
          .filter((f) => f.visible)
          .map((f) =>
            f.kind === 'bool' ? (
              <SpotSummaryRow
                key={f.title.id}
                title={f.title}
                help={f.help}
                data={is15M ? f.data15M : f.data60M}
                pricePeriod={spotPricePeriod}
                Cell={ColBoolean}
                getTooltip={getTooltip}
              />
            ) : (
              <SpotSummaryRow
                key={f.title.id}
                title={f.title}
                help={f.help}
                data={f.data60M}
                pricePeriod={SpotPricePeriod['60M']}
                Cell={ColHeatPumpLevel}
                getTooltip={getTooltip}
              />
            )
          )}
      </Box>
    </Paper>
  );
};
